import React, { useState, useRef, useEffect } from 'react';
import { X, ZoomIn, ZoomOut, RotateCw, Upload, Check } from 'lucide-react';

const ImageUploadWithPreview = ({ onImageSelect, currentImage, aspectRatio = 1 }) => {
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [zoom, setZoom] = useState(1);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [rotation, setRotation] = useState(0);
    const [error, setError] = useState('');
    const [isCompressing, setIsCompressing] = useState(false);
    
    const canvasRef = useRef(null);
    const imageRef = useRef(null);
    const containerRef = useRef(null);

    const TARGET_SIZE = 1 * 1024 * 1024; // 1MB in bytes - target for compressed image
    const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png'];

    const validateFile = (file) => {
        // Check file type
        if (!ALLOWED_TYPES.includes(file.type)) {
            setError('Please select a JPEG, JPG, or PNG image');
            return false;
        }

        // No size limit - we'll compress it
        setError('');
        return true;
    };

    const compressImage = (dataUrl, targetSizeBytes) => {
        return new Promise((resolve) => {
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                
                // Start with original dimensions
                let width = img.width;
                let height = img.height;
                
                // Set canvas size
                canvas.width = width;
                canvas.height = height;
                
                // Draw image
                ctx.drawImage(img, 0, 0, width, height);
                
                // Try different quality levels to get under target size
                let quality = 0.9;
                let compressed = canvas.toDataURL('image/jpeg', quality);
                
                // Reduce quality until under target size
                while (compressed.length > targetSizeBytes * 1.37 && quality > 0.1) {
                    quality -= 0.05;
                    compressed = canvas.toDataURL('image/jpeg', quality);
                }
                
                // If still too large, reduce dimensions
                if (compressed.length > targetSizeBytes * 1.37) {
                    const scale = Math.sqrt(targetSizeBytes * 1.37 / compressed.length);
                    width = Math.floor(width * scale);
                    height = Math.floor(height * scale);
                    
                    canvas.width = width;
                    canvas.height = height;
                    ctx.drawImage(img, 0, 0, width, height);
                    
                    compressed = canvas.toDataURL('image/jpeg', 0.9);
                }
                
                resolve(compressed);
            };
            img.src = dataUrl;
        });
    };

    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!validateFile(file)) {
            e.target.value = ''; // Reset file input
            return;
        }

        setSelectedFile(file);
        const reader = new FileReader();
        reader.onloadend = () => {
            setPreviewUrl(reader.result);
            setShowPreviewModal(true);
            setZoom(1);
            setPosition({ x: 0, y: 0 });
            setRotation(0);
        };
        reader.readAsDataURL(file);
    };

    const handleZoomIn = () => {
        setZoom(prev => Math.min(prev + 0.1, 3));
    };

    const handleZoomOut = () => {
        setZoom(prev => Math.max(prev - 0.1, 0.5));
    };

    const handleRotate = () => {
        setRotation(prev => (prev + 90) % 360);
    };

    const handleMouseDown = (e) => {
        setIsDragging(true);
        setDragStart({
            x: e.clientX - position.x,
            y: e.clientY - position.y
        });
    };

    const handleMouseMove = (e) => {
        if (!isDragging) return;
        setPosition({
            x: e.clientX - dragStart.x,
            y: e.clientY - dragStart.y
        });
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    const handleTouchStart = (e) => {
        const touch = e.touches[0];
        setIsDragging(true);
        setDragStart({
            x: touch.clientX - position.x,
            y: touch.clientY - position.y
        });
    };

    const handleTouchMove = (e) => {
        if (!isDragging) return;
        const touch = e.touches[0];
        setPosition({
            x: touch.clientX - dragStart.x,
            y: touch.clientY - dragStart.y
        });
    };

    const handleTouchEnd = () => {
        setIsDragging(false);
    };

    const getCroppedImage = () => {
        if (!canvasRef.current || !imageRef.current) return null;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const image = imageRef.current;

        // Set canvas size to be square for circular crop (matches the 70% circular frame)
        const size = 400;
        canvas.width = size;
        canvas.height = size;

        // Clear canvas with transparent background
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // Calculate the circular crop area (70% of container, same as preview)
        const containerWidth = containerRef.current.offsetWidth;
        const containerHeight = containerRef.current.offsetHeight;
        const circleRadius = (canvas.width / 2) * (70 / 100); // 70% to match preview
        const scale = zoom;

        // Save context state
        ctx.save();

        // Move to center for all transformations
        ctx.translate(canvas.width / 2, canvas.height / 2);
        
        // Apply rotation
        ctx.rotate((rotation * Math.PI) / 180);

        // Create circular clipping path (circle that touches all edges of the square)
        // We'll scale up so the 70% preview circle becomes 100% of output
        const cropScale = 100 / 70; // Scale factor to make 70% circle fill 100% of canvas
        ctx.scale(cropScale, cropScale);
        
        ctx.beginPath();
        ctx.arc(0, 0, circleRadius, 0, Math.PI * 2);
        ctx.closePath();
        ctx.clip();

        // Draw image
        const scaledWidth = image.naturalWidth * scale * (containerWidth / image.naturalWidth);
        const scaledHeight = image.naturalHeight * scale * (containerHeight / image.naturalHeight);
        
        ctx.drawImage(
            image,
            -scaledWidth / 2 + (position.x / containerWidth) * scaledWidth,
            -scaledHeight / 2 + (position.y / containerHeight) * scaledHeight,
            scaledWidth,
            scaledHeight
        );

        // Restore context state
        ctx.restore();

        // Return as JPEG (background will be white outside circle, but with rounded-full CSS it won't show)
        return canvas.toDataURL('image/jpeg', 0.95);
    };

    const handleConfirm = async () => {
        setIsCompressing(true);
        try {
            const croppedImage = getCroppedImage();
            if (croppedImage) {
                // Compress the image to be under 1MB
                const compressedImage = await compressImage(croppedImage, TARGET_SIZE);
                onImageSelect(compressedImage);
                setShowPreviewModal(false);
                setPreviewUrl(null);
                setSelectedFile(null);
            }
        } catch (error) {
            console.error('Error compressing image:', error);
            setError('Failed to compress image. Please try again.');
        } finally {
            setIsCompressing(false);
        }
    };

    const handleCancel = () => {
        setShowPreviewModal(false);
        setPreviewUrl(null);
        setSelectedFile(null);
        setZoom(1);
        setPosition({ x: 0, y: 0 });
        setRotation(0);
        setError('');
    };

    return (
        <>
            {/* Upload Button */}
            <div>
                <label className="cursor-pointer bg-primary-blue text-white px-4 py-2 rounded-lg hover:bg-opacity-90 transition-all inline-flex items-center gap-2">
                    <Upload className="w-4 h-4" />
                    <input
                        type="file"
                        accept=".jpg,.jpeg,.png"
                        onChange={handleFileSelect}
                        className="hidden"
                    />
                    Upload Photo
                </label>
                {error && (
                    <p className="text-red-600 text-sm mt-2">{error}</p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                    JPEG, JPG, or PNG • Auto-compressed to optimize size
                </p>
            </div>

            {/* Preview Modal */}
            {showPreviewModal && previewUrl && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black bg-opacity-75">
                    <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl">
                        {/* Header */}
                        <div className="flex items-center justify-between p-4 border-b border-gray-200">
                            <h3 className="text-lg font-bold text-gray-800">
                                Adjust Your Photo
                            </h3>
                            <button
                                onClick={handleCancel}
                                className="text-gray-500 hover:text-gray-700 transition-colors"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        {/* Preview Area */}
                        <div className="p-6">
                            <div className="flex justify-center items-center">
                                <div
                                    ref={containerRef}
                                    className="relative w-96 h-96 bg-gray-100 rounded-lg overflow-hidden cursor-move"
                                    onMouseDown={handleMouseDown}
                                    onMouseMove={handleMouseMove}
                                    onMouseUp={handleMouseUp}
                                    onMouseLeave={handleMouseUp}
                                    onTouchStart={handleTouchStart}
                                    onTouchMove={handleTouchMove}
                                    onTouchEnd={handleTouchEnd}
                                    style={{ maxWidth: '100%', aspectRatio: '1/1' }}
                                >
                                    <img
                                        ref={imageRef}
                                        src={previewUrl}
                                        alt="Preview"
                                        className="absolute inset-0 w-full h-full object-contain"
                                        style={{
                                            transform: `scale(${zoom}) translate(${position.x / zoom}px, ${position.y / zoom}px) rotate(${rotation}deg)`,
                                            transformOrigin: 'center',
                                            transition: isDragging ? 'none' : 'transform 0.1s ease-out',
                                            userSelect: 'none',
                                            pointerEvents: 'none'
                                        }}
                                    />
                                    
                                    {/* Circular Frame Overlay */}
                                    <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                                        <div className="relative" style={{ width: '70%', paddingBottom: '70%' }}>
                                            <div className="absolute inset-0">
                                                {/* White circle border */}
                                                <div className="absolute inset-0 rounded-full border-2 border-white" style={{ boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.6)' }}></div>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* Grid Overlay inside circular frame */}
                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                        <div style={{ width: '70%', paddingBottom: '70%' }} className="relative">
                                            <div className="absolute inset-0 rounded-full overflow-hidden">
                                                <div className="w-full h-full grid grid-cols-3 grid-rows-3">
                                                    {[...Array(9)].map((_, i) => (
                                                        <div key={i} className="border border-white border-opacity-30"></div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Controls */}
                            <div className="flex items-center justify-center gap-4 mt-4">
                                <button
                                    type="button"
                                    onClick={handleZoomOut}
                                    className="p-3 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                                    title="Zoom Out"
                                >
                                    <ZoomOut className="w-5 h-5 text-gray-700" />
                                </button>
                                
                                <div className="flex items-center gap-2">
                                    <span className="text-sm text-gray-600 font-medium">
                                        {Math.round(zoom * 100)}%
                                    </span>
                                    <input
                                        type="range"
                                        min="0.5"
                                        max="3"
                                        step="0.1"
                                        value={zoom}
                                        onChange={(e) => setZoom(parseFloat(e.target.value))}
                                        className="w-32"
                                    />
                                </div>
                                
                                <button
                                    type="button"
                                    onClick={handleZoomIn}
                                    className="p-3 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                                    title="Zoom In"
                                >
                                    <ZoomIn className="w-5 h-5 text-gray-700" />
                                </button>
                                
                                <div className="w-px h-8 bg-gray-300"></div>
                                
                                <button
                                    type="button"
                                    onClick={handleRotate}
                                    className="p-3 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                                    title="Rotate 90°"
                                >
                                    <RotateCw className="w-5 h-5 text-gray-700" />
                                </button>
                            </div>

                            <p className="text-center text-sm text-gray-600 mt-3">
                                Drag to reposition • Scroll or use slider to zoom • Click rotate to turn
                            </p>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-3 p-4 border-t border-gray-200">
                            <button
                                type="button"
                                onClick={handleCancel}
                                disabled={isCompressing}
                                className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleConfirm}
                                disabled={isCompressing}
                                className="flex-1 px-6 py-3 bg-primary-blue text-white rounded-lg font-semibold hover:scale-105 transform transition-all duration-200 shadow-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                            >
                                {isCompressing ? (
                                    <>
                                        <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Compressing...
                                    </>
                                ) : (
                                    <>
                                        <Check className="w-5 h-5" />
                                        Confirm
                                    </>
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Hidden canvas for cropping */}
                    <canvas ref={canvasRef} style={{ display: 'none' }} />
                </div>
            )}
        </>
    );
};

export default ImageUploadWithPreview;

